"use client"

import type { LucideIcon } from "lucide-react"

import { cn } from "@ibada/ui"

import { useLocale } from "@/i18n"
import { PersonPreviewCell } from "@/shared/components/person-preview"

/** One thing that happened to a record: who did it, when, and what they said about it. */
export interface EntityTimelineItem {
  id: string
  icon: LucideIcon
  /** Tailwind classes for the marker chip, e.g. `bg-emerald-500/15 text-emerald-500`. */
  tone: string
  label: string
  at: string
  actorUserId?: string | null
  actorName?: string | null
  actorAvatarUrl?: string | null
  note?: string | null
}

/**
 * A record's lifecycle as a vertical rail.
 *
 * Extracted from the reconciliation timeline so transfers and reconciliations read identically — both answer
 * the same question ("who did what to this, and when"), and two hand-rolled versions would drift the moment
 * one gained a detail the other did not.
 *
 * Callers own the vocabulary: they map their own events to labels, icons and tones. This owns only the rail,
 * the actor chip and the note treatment.
 */
export function EntityTimeline({ items, systemLabel }: { items: EntityTimelineItem[]; systemLabel: string }) {
  const locale = useLocale()

  const formatDateTime = (iso: string) => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date)
  }

  return (
    <ol className='relative'>
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        const Icon = item.icon
        return (
          <li key={item.id} className='flex gap-3'>
            {/* The rail: a marker per event, joined by a line that stops at the last one. */}
            <div className='flex flex-col items-center self-stretch'>
              <span className={cn("flex size-6 shrink-0 items-center justify-center rounded-full", item.tone)}>
                <Icon className='size-3' />
              </span>
              {!isLast ? <span className='bg-border mt-1 w-px flex-1' aria-hidden /> : null}
            </div>

            <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-4")}>
              <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                <span className='text-sm font-medium'>{item.label}</span>
                <span className='text-muted-foreground text-xs'>{formatDateTime(item.at)}</span>
              </div>

              <ActorChip
                userId={item.actorUserId ?? null}
                name={item.actorName ?? null}
                avatarUrl={item.actorAvatarUrl ?? null}
                systemLabel={systemLabel}
              />

              {/* The note is the substance of the event — the reason a decision went the way it did. */}
              {item.note ? (
                <p className='bg-muted/40 mt-1.5 rounded-md border px-2.5 py-1.5 text-sm whitespace-pre-wrap'>
                  {item.note}
                </p>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function ActorChip({
  userId,
  name,
  avatarUrl,
  systemLabel,
}: {
  userId: string | null
  name: string | null
  avatarUrl: string | null
  systemLabel: string
}) {
  return (
    <span className='mt-0.5 inline-flex max-w-full text-muted-foreground text-xs'>
      <PersonPreviewCell
        identity={{ userId }}
        name={name ?? systemLabel}
        avatarUrl={avatarUrl}
        enablePreview={false}
      />
    </span>
  )
}
